import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Calendar, CreditCard, Shield, X } from 'lucide-react';

const CancelModal = ({ isOpen, onClose, onConfirm, subscription, isCancelling = false }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isCancelling) onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, isCancelling, onClose]);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'End of current billing period';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'End of current billing period';
    }
  };

  const planName = subscription?.plan_name || 'Your current plan';
  const accessUntil = formatDate(subscription?.next_billing_date);
  const statusLabel = (subscription?.status || 'active').toString();

  const modalContent = (
    <div
      className="candor-cancel-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isCancelling) onClose();
      }}
    >
      <div
        className="candor-cancel-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-subscription-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="candor-cancel-modal__header">
          <div className="candor-cancel-modal__header-text">
            <div className="candor-cancel-modal__icon" aria-hidden>
              <AlertTriangle className="w-6 h-6" strokeWidth={2} />
            </div>
            <div>
              <h2 id="cancel-subscription-title" className="candor-cancel-modal__title">
                Cancel subscription
              </h2>
              <p className="candor-cancel-modal__subtitle">
                Review what happens before you confirm
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isCancelling}
            className="candor-cancel-modal__close"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="candor-cancel-modal__body">
          <div className="candor-cancel-modal__notice">
            <p className="candor-cancel-modal__notice-title">You will keep access until your period ends</p>
            <p className="candor-cancel-modal__notice-text">
              Cancellation stops future renewals only. Premium features remain available until the date
              below, then your account moves to limited access.
            </p>
          </div>

          <dl className="candor-cancel-modal__details">
            <div className="candor-cancel-modal__detail-row">
              <dt>
                <CreditCard className="w-4 h-4" aria-hidden />
                Current plan
              </dt>
              <dd>{planName}</dd>
            </div>
            <div className="candor-cancel-modal__detail-row">
              <dt>
                <Shield className="w-4 h-4" aria-hidden />
                Status
              </dt>
              <dd className="capitalize">{statusLabel}</dd>
            </div>
            <div className="candor-cancel-modal__detail-row">
              <dt>
                <Calendar className="w-4 h-4" aria-hidden />
                Access until
              </dt>
              <dd>{accessUntil}</dd>
            </div>
          </dl>

          <ul className="candor-cancel-modal__list">
            <li>No further charges after the current billing period</li>
            <li>You can resubscribe anytime from Plans &amp; pricing</li>
            <li>Unused tokens may not roll over after plan changes</li>
          </ul>
        </div>

        <footer className="candor-cancel-modal__footer">
          <button
            type="button"
            onClick={onClose}
            disabled={isCancelling}
            className="candor-cancel-modal__btn candor-cancel-modal__btn--primary"
          >
            Keep subscription
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isCancelling}
            className="candor-cancel-modal__btn candor-cancel-modal__btn--danger"
          >
            {isCancelling ? (
              <>
                <span className="candor-cancel-modal__spinner" aria-hidden />
                Processing cancellation…
              </>
            ) : (
              'Confirm cancellation'
            )}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CancelModal;
